import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { WbSunnyOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { makeApiCall } from '../../config/api';

const MorningBriefPanel = () => {
  const { t } = useTranslation();
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadBrief = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await makeApiCall('api/insights/morning-brief');
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || t('insights.morning.loadFailed'));
      setBrief(payload);
    } catch (loadError) {
      setError(loadError.message || t('insights.morning.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadBrief();
  }, [loadBrief]);

  const routes = brief?.at_risk_routes || [];

  return (
    <Card variant="outlined" component="section" aria-labelledby="morning-brief-title" sx={{ mb: 2 }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <WbSunnyOutlined sx={{ color: '#b54708' }} aria-hidden="true" />
          <Typography id="morning-brief-title" variant="h6" component="h3">
            {t('insights.morning.title')}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('insights.morning.description')}
        </Typography>

        {loading && (
          <Stack alignItems="center" spacing={1} sx={{ py: 3 }}>
            <CircularProgress size={28} aria-label={t('insights.morning.loadingAria')} />
            <Typography variant="body2" color="text.secondary">
              {t('insights.morning.loading')}
            </Typography>
          </Stack>
        )}

        {!loading && error && (
          <Alert
            severity="error"
            action={(
              <Button color="inherit" size="small" onClick={loadBrief} aria-label={t('insights.morning.retryAria')}>
                {t('insights.retry')}
              </Button>
            )}
          >
            {error}
          </Alert>
        )}

        {!loading && !error && routes.length === 0 && (
          <Alert severity="success">{t('insights.morning.noneAtRisk')}</Alert>
        )}

        {!loading && !error && routes.length > 0 && (
          <Stack spacing={1.25} component="ul" sx={{ m: 0, pl: 0, listStyle: 'none' }}>
            {routes.map((route) => {
              const percent = Math.round(Number(route.risk_probability || 0) * 100);
              const overtime = Math.round(Number(route.overtime_minutes || 0));
              return (
                <Box
                  key={`${route.truck_id}-${route.scheduled_date}`}
                  component="li"
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: route.risk_band === 'high' ? '#ef9a9a' : '#ffe082',
                    bgcolor: route.risk_band === 'high' ? '#fff5f5' : '#fff8e1',
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                  >
                    <Box>
                      <Typography variant="subtitle1" component="h4" sx={{ fontWeight: 700 }}>
                        {route.truck_name || t('insights.morning.unnamedTruck', { id: route.truck_id })}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('insights.morning.routeSummary', {
                          percent,
                          overtime,
                          date: route.scheduled_date,
                        })}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={t(`insights.priorities.${route.risk_band || 'medium'}`)}
                      aria-label={t('insights.morning.riskAria', {
                        truck: route.truck_name || route.truck_id,
                        band: route.risk_band,
                      })}
                      sx={{
                        bgcolor: route.risk_band === 'high' ? '#c62828' : '#b54708',
                        color: '#fff',
                        fontWeight: 700,
                      }}
                    />
                  </Stack>
                  <Button
                    size="small"
                    href="/dashboard/routes-list"
                    sx={{ mt: 1 }}
                    aria-label={t('insights.morning.openRouteAria', {
                      truck: route.truck_name || route.truck_id,
                    })}
                  >
                    {t('insights.morning.openRoute')}
                  </Button>
                </Box>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default MorningBriefPanel;
